import * as React from "react";
import { ComponentType, useState } from "react";
import {
  Form,
  FormDataConsumer,
  useNotify,
  useRedirect,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RecordSelector from "./RecordSelector";
import { getErrorMessage } from "./getErrorMessage";
import { WithId } from "../../types";

interface SelectorConfig<T extends WithId> {
  resource: string;
  getOptionLabel: (option: T) => string | undefined;
  labelKey: string;
  placeholderKey: string;
}

interface LoadRecordResult<EditingIds> {
  defaultValues: Record<string, any>;
  editingIds: EditingIds;
}

interface SaveRecordResult {
  resource: string;
  id: string;
}

interface CreatorPageProps<T extends WithId, EditingIds> {
  titleKey: string;
  subtitleKey: string;
  saveKey: string;
  saveExistingKey: string;
  successKey: string;
  errorKey: string;
  loadErrorKey: string;
  selector: SelectorConfig<T>;
  emptyDefaults: Record<string, any>;
  loadRecord: (option: T) => Promise<LoadRecordResult<EditingIds>>;
  saveRecord: (
    values: Record<string, any>,
    editingIds: EditingIds | null
  ) => Promise<SaveRecordResult>;
  BuilderForm: ComponentType;
  RenderPreview: ComponentType<{ formData: Record<string, any> }>;
}

// Generic shell for a "creator": a page that composes several legacy
// resources (a banner, some section entities, ...) into one form with a
// form/preview toggle, and can load an existing combo back in for editing.
// Each creator (services/mainPage/faq) supplies the resource-specific
// pieces (selector, defaults, load/save) and gets the same shell, so the
// orchestration (loading state, edit-vs-create, remount-on-switch) only
// has to be gotten right once.
const CreatorPage = <T extends WithId, EditingIds,>({
  titleKey,
  subtitleKey,
  saveKey,
  saveExistingKey,
  successKey,
  errorKey,
  loadErrorKey,
  selector,
  emptyDefaults,
  loadRecord,
  saveRecord,
  BuilderForm,
  RenderPreview,
}: CreatorPageProps<T, EditingIds>) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const [mode, setMode] = useState<"form" | "preview">("form");
  const [saving, setSaving] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  // editingIds is null while composing a brand new page, and holds the
  // known ids of every sub-record once an existing one is loaded for edit.
  const [editingIds, setEditingIds] = useState<EditingIds | null>(null);
  const [loadedDefaults, setLoadedDefaults] = useState<Record<
    string,
    any
  > | null>(null);
  const [selectedOption, setSelectedOption] = useState<T | null>(null);

  const handleSelect = async (option: T | null) => {
    setSelectedOption(option);

    if (!option) {
      setEditingIds(null);
      setLoadedDefaults(null);
      return;
    }

    setLoadingRecord(true);
    try {
      const { defaultValues, editingIds: ids } = await loadRecord(option);
      setLoadedDefaults(defaultValues);
      setEditingIds(ids);
    } catch (error) {
      notify(loadErrorKey, {
        type: "error",
        messageArgs: { error: getErrorMessage(error, translate) },
      });
      setEditingIds(null);
      setLoadedDefaults(null);
      setSelectedOption(null);
    } finally {
      setLoadingRecord(false);
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    setSaving(true);
    try {
      const { resource, id } = await saveRecord(values, editingIds);
      notify(successKey, { type: "success" });
      redirect("edit", resource, id);
    } catch (error) {
      notify(errorKey, {
        type: "error",
        messageArgs: { error: getErrorMessage(error, translate) },
      });
    } finally {
      setSaving(false);
    }
  };

  const formDisabled = saving || loadingRecord;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", mt: 2, mb: 6 }}>
      <Typography variant="h5" gutterBottom>
        {translate(titleKey)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate(subtitleKey)}
      </Typography>

      <RecordSelector<T>
        resource={selector.resource}
        getOptionLabel={selector.getOptionLabel}
        labelKey={selector.labelKey}
        placeholderKey={selector.placeholderKey}
        value={selectedOption}
        onChange={handleSelect}
        disabled={saving}
      />
      {loadingRecord && <LinearProgress sx={{ mt: 1 }} />}

      <Form
        key={editingIds ? JSON.stringify(editingIds) : "new"}
        defaultValues={loadedDefaults ?? emptyDefaults}
        onSubmit={handleSubmit}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mt: 3,
            mb: 3,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            py: 1,
            px: 2,
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(event, value) => value && setMode(value)}
            size="small"
          >
            <ToggleButton value="form">
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              {translate("creators.tabs.form")}
            </ToggleButton>
            <ToggleButton value="preview">
              <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
              {translate("creators.tabs.preview")}
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            type="submit"
            variant="contained"
            disabled={formDisabled}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {translate(editingIds ? saveExistingKey : saveKey)}
          </Button>
        </Stack>

        {mode === "form" ? (
          <BuilderForm />
        ) : (
          <FormDataConsumer>
            {({ formData }) => <RenderPreview formData={formData} />}
          </FormDataConsumer>
        )}
      </Form>
    </Box>
  );
};

export default CreatorPage;
